import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { AuthImage } from "@/components/AuthImage";
import { money } from "@/lib/pricing";

export default function Deals() {
  const { selectedBusinessId } = useAuth();
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedBusinessId) return;
    api.listSpecials(selectedBusinessId).then(setSpecials).catch(() => {}).finally(() => setLoading(false));
  }, [selectedBusinessId]);

  return (
    <div className="container-app py-8 sm:py-12">
      <div className="flex items-center gap-2">
        <Sparkles style={{ color: "var(--accent)" }} />
        <h1 className="font-display text-4xl" style={{ color: "var(--ink)" }}>Deals & specials</h1>
      </div>
      <p className="mt-1" style={{ color: "var(--muted)" }}>Limited-time offers, freshly out of the oven.</p>

      {loading ? (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-72 rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />
          ))}
        </div>
      ) : specials.length === 0 ? (
        <div className="mt-10 text-center py-16 rounded-2xl" style={{ background: "var(--surface)", border: "1px dashed var(--border)" }}>
          <p className="font-display text-xl" style={{ color: "var(--ink)" }}>No specials right now</p>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Check back soon for fresh deals.</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {specials.map((s, i) => {
            const card = (
              <div
                className="rounded-2xl overflow-hidden card-lift h-full animate-fade-up"
                style={{ background: "var(--surface)", border: "1px solid var(--border)", opacity: s.is_ended ? 0.7 : 1, animationDelay: `${i * 40}ms` }}
              >
                <div className="relative aspect-[16/10]" style={{ background: "var(--surface-2)" }}>
                  {s.image_url ? (
                    <AuthImage src={s.image_url} alt={s.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center"><Sparkles size={40} style={{ color: "var(--accent)" }} /></div>
                  )}
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase" style={{ background: s.is_ended ? "#9ca3af" : "var(--accent)", color: "#1a1400" }}>
                    {s.is_ended ? "Ended" : s.badge_text || "Special"}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl" style={{ color: "var(--ink)" }}>{s.title}</h3>
                  <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--muted)" }}>{s.description}</p>
                  {s.sale_price != null && (
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="font-display text-xl" style={{ color: "var(--brand)" }}>{money(s.sale_price)}</span>
                      {s.original_price != null && <span className="text-sm line-through" style={{ color: "var(--muted)" }}>{money(s.original_price)}</span>}
                    </div>
                  )}
                  {s.valid_until && (
                    <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                      {s.is_ended ? "Ended" : "Valid until"} {new Date(s.valid_until).toLocaleDateString()}
                    </div>
                  )}
                  {s.product_id && !s.is_ended && (
                    <Link to={`/product/${s.product_id}`} className="inline-block mt-3 text-sm font-semibold underline" style={{ color: "var(--brand)" }}>
                      Order now
                    </Link>
                  )}
                </div>
              </div>
            );
            return <div key={s.id} data-testid={`special-${s.id}`}>{card}</div>;
          })}
        </div>
      )}
    </div>
  );
}
